import { useTranslation } from "react-i18next";
import {
  Banknote,
  Fingerprint,
  Calendar,
  Users,
  UserPlus,
  Route,
  Target,
  GraduationCap,
  Heart,
  Wallet,
  FileSignature,
  ShieldCheck,
  MessageSquare,
  Bot,
  Smartphone,
} from "lucide-react";
import FadeInUp from "./FadeInUp";

// Badge palette by maturity
const CORE = "#27a644";    // proven, in production
const AI = "#5e6ad2";      // AI-powered
const NEW = "#828fff";     // recently shipped module

const FEATURES = [
  {
    icon: Banknote,
    badge: "Core · IA",
    badgeColor: AI,
    title: { fr: "Paie Multi-Pays", en: "Multi-Country Payroll", ar: "رواتب متعددة الدول" },
    body:  { fr: "Calculateurs natifs pour la Tunisie (CNSS, IRPP), le Qatar (GOSI), le Maroc (CNSS/IR), la France (DSN) et l'Arabie Saoudite. Détection d'anomalies par IA.", en: "Native calculators for Tunisia (CNSS, IRPP), Qatar (GOSI), Morocco (CNSS/IR), France (DSN) and Saudi Arabia. AI-powered anomaly detection.", ar: "حاسبات أصلية لتونس وقطر والمغرب وفرنسا والسعودية، مع كشف الشذوذات بالذكاء الاصطناعي." },
  },
  {
    icon: Fingerprint,
    badge: "Core",
    badgeColor: CORE,
    title: { fr: "Présence ZKTeco", en: "ZKTeco Attendance", ar: "حضور ZKTeco" },
    body:  { fr: "Intégration native avec les terminaux biométriques via protocole ADMS. Synchronisation temps réel, multi-sites.", en: "Native integration with ZKTeco biometric terminals via ADMS protocol. Real-time, multi-site synchronization.", ar: "تكامل أصلي مع أجهزة ZKTeco البيومترية عبر بروتوكول ADMS. مزامنة فورية متعددة المواقع." },
  },
  {
    icon: Smartphone,
    badge: "Mobile",
    badgeColor: NEW,
    title: { fr: "Application Mobile", en: "Mobile App", ar: "تطبيق الجوال" },
    body:  { fr: "Apps iOS et Android : pointage géolocalisé, demandes de congés, validations et notifications push — où que vous soyez.", en: "iOS and Android apps: geolocated check-in, leave requests, approvals and push notifications — wherever you are.", ar: "تطبيقات iOS و Android: تسجيل حضور محدد الموقع وطلبات الإجازة والموافقات وإشعارات فورية أينما كنت." },
  },
  {
    icon: Calendar,
    badge: "Core",
    badgeColor: CORE,
    title: { fr: "Congés & Approbations", en: "Leave & Approvals", ar: "الإجازات والموافقات" },
    body:  { fr: "Circuits d'approbation configurables à plusieurs niveaux, politiques de congés et calendriers de jours fériés par pays.", en: "Configurable multi-level approval circuits, leave policies and public holiday calendars by country.", ar: "دوائر موافقة قابلة للتكوين متعددة المستويات وسياسات إجازات وتقويمات عطل حسب الدولة." },
  },
  {
    icon: Users,
    badge: "Core",
    badgeColor: CORE,
    title: { fr: "Dossier Employé & Organigramme", en: "Employee File & Org Chart", ar: "ملف الموظف والهيكل التنظيمي" },
    body:  { fr: "Photo, contrats, historique, biométrie et plannings centralisés. Organigramme interactif avec contrôle d'accès par rôle.", en: "Photo, contracts, history, biometrics and schedules centralized. Interactive org chart with role-based access control.", ar: "الصور والعقود والتاريخ والبيومترية والجداول مركزية، مع هيكل تنظيمي تفاعلي وتحكم في الوصول حسب الدور." },
  },
  {
    icon: UserPlus,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Recrutement & ATS", en: "Recruitment & ATS", ar: "التوظيف ونظام التتبع" },
    body:  { fr: "Offres d'emploi, portail carrières public, pipeline kanban des candidats, planification d'entretiens et matching IA.", en: "Job postings, public careers portal, kanban candidate pipeline, interview scheduling and AI matching.", ar: "إعلانات وظائف وبوابة توظيف عامة وخط أنابيب للمرشحين وجدولة مقابلات ومطابقة بالذكاء الاصطناعي." },
  },
  {
    icon: Route,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Onboarding & Offboarding", en: "Onboarding & Offboarding", ar: "الإلحاق وإنهاء الخدمة" },
    body:  { fr: "Parcours d'intégration et de départ structurés : tâches, check-lists, documents et suivi automatisés.", en: "Structured joining and exit journeys: automated tasks, checklists, documents and tracking.", ar: "مسارات منظمة للالتحاق والمغادرة: مهام وقوائم مراجعة ووثائق ومتابعة آلية." },
  },
  {
    icon: Target,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Performance & Succession", en: "Performance & Succession", ar: "الأداء والخلافة" },
    body:  { fr: "Cycles d'évaluation, OKR, feedback, entretiens individuels, matrices de compétences et plans de succession.", en: "Review cycles, OKRs, feedback, one-on-ones, skills matrices and succession planning.", ar: "دورات تقييم وأهداف وتغذية راجعة ومقابلات فردية ومصفوفات مهارات وخطط خلافة." },
  },
  {
    icon: GraduationCap,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Formation & LMS", en: "Learning & LMS", ar: "التدريب ونظام التعلم" },
    body:  { fr: "Catalogue de formations, parcours d'apprentissage, suivi des compétences et plans de développement.", en: "Training catalog, learning paths, skills tracking and development plans.", ar: "كتالوج تدريب ومسارات تعلم وتتبع المهارات وخطط التطوير." },
  },
  {
    icon: Heart,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Engagement & Bien-être", en: "Engagement & Wellbeing", ar: "المشاركة والرفاهية" },
    body:  { fr: "Sondages d'engagement, reconnaissance entre pairs et suivi du bien-être des collaborateurs.", en: "Engagement surveys, peer recognition and employee wellbeing tracking.", ar: "استبيانات المشاركة والتقدير بين الزملاء ومتابعة رفاهية الموظفين." },
  },
  {
    icon: Wallet,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Rémunération & Avantages", en: "Compensation & Benefits", ar: "التعويضات والمزايا" },
    body:  { fr: "Gestion des grilles de rémunération, avantages sociaux et notes de frais avec circuits de validation.", en: "Compensation bands, benefits administration and expense claims with approval flows.", ar: "إدارة سلالم الأجور والمزايا الاجتماعية ومطالبات النفقات مع دوائر الموافقة." },
  },
  {
    icon: FileSignature,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Contrats & E-Signature", en: "Contracts & E-Signature", ar: "العقود والتوقيع الإلكتروني" },
    body:  { fr: "Génération de contrats assistée par IA et signature électronique intégrée (OTP, certificat, empreinte) ou via Yousign.", en: "AI-assisted contract generation and built-in e-signature (OTP, certificate, hash) or via Yousign.", ar: "توليد العقود بمساعدة الذكاء الاصطناعي وتوقيع إلكتروني مدمج أو عبر Yousign." },
  },
  {
    icon: ShieldCheck,
    badge: "Nouveau",
    badgeColor: NEW,
    title: { fr: "Conformité & RGPD", en: "Compliance & GDPR", ar: "الامتثال وحماية البيانات" },
    body:  { fr: "Outils de conformité, gestion des droits RGPD, piste d'audit complète et déclarations sociales (DSN France).", en: "Compliance tooling, GDPR rights management, full audit trail and social declarations (French DSN).", ar: "أدوات امتثال وإدارة حقوق حماية البيانات وسجل تدقيق كامل وإقرارات اجتماعية." },
  },
  {
    icon: MessageSquare,
    badge: "IA",
    badgeColor: AI,
    title: { fr: "Assistant RH Conversationnel", en: "Conversational HR Assistant", ar: "مساعد الموارد البشرية التحادثي" },
    body:  { fr: "Posez vos questions RH en langage naturel et obtenez des analyses instantanées sur vos données en temps réel.", en: "Ask HR questions in natural language and get instant analytics on your live data.", ar: "اطرح أسئلتك بلغة طبيعية واحصل على تحليلات فورية على بياناتك الحية." },
  },
  {
    icon: Bot,
    badge: "IA",
    badgeColor: AI,
    title: { fr: "Agents IA Autonomes", en: "Autonomous AI Agents", ar: "وكلاء الذكاء الاصطناعي" },
    body:  { fr: "Agents qui surveillent les contrats expirants, réconcilient les anomalies de pointage et assistent la clôture de paie.", en: "Agents that watch expiring contracts, reconcile attendance anomalies and assist payroll closing.", ar: "وكلاء يراقبون العقود المنتهية ويعالجون شذوذات الحضور ويساعدون في إقفال الرواتب." },
  },
];

const LandingFeatures = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const t = (obj) => obj[lang] || obj.fr;

  return (
    <section id="features" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <FadeInUp>
          <div style={{ textAlign: "center", maxWidth: "560px", margin: "0 auto 64px" }}>
            <span
              style={{
                display: "inline-block",
                fontSize: "12px",
                fontWeight: 500,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                padding: "4px 14px",
                borderRadius: "9999px",
                color: "#5e6ad2",
                backgroundColor: "rgba(94,106,210,0.10)",
                border: "1px solid rgba(94,106,210,0.20)",
                marginBottom: "20px",
              }}
            >
              {t({ fr: "Fonctionnalités", en: "Features", ar: "الميزات" })}
            </span>
            <h2
              style={{
                color: "#f7f8f8",
                fontSize: "clamp(28px, 4vw, 40px)",
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: "-1.0px",
                marginBottom: "16px",
              }}
            >
              {t({ fr: "Tout ce dont votre équipe RH a besoin", en: "Everything your HR team needs", ar: "كل ما يحتاجه فريقك" })}
            </h2>
            <p style={{ color: "#8a8f98", fontSize: "16px", lineHeight: 1.5, letterSpacing: "-0.05px" }}>
              {t({
                fr: "Un seul outil pour gérer l'ensemble du cycle de vie employé, de l'embauche à la paie.",
                en: "A single tool to manage the entire employee lifecycle, from hiring to payroll.",
                ar: "أداة واحدة لإدارة دورة حياة الموظف بالكامل، من التوظيف إلى الرواتب.",
              })}
            </p>
          </div>
        </FadeInUp>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <FadeInUp key={i} delay={i * 0.06}>
                <div
                  style={{
                    backgroundColor: "#0f1011",
                    border: "1px solid #23252a",
                    borderRadius: "12px",
                    padding: "24px",
                    height: "100%",
                    transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(94,106,210,0.4)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(94,106,210,0.1), 0 0 0 1px rgba(94,106,210,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#23252a";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {/* Badge */}
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 600,
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                      padding: "2px 7px",
                      borderRadius: "9999px",
                      color: feat.badgeColor,
                      backgroundColor: feat.badgeColor + "20",
                      border: `1px solid ${feat.badgeColor}30`,
                      marginBottom: "12px",
                      display: "inline-block",
                    }}
                  >
                    {feat.badge}
                  </span>

                  {/* Icon */}
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, rgba(94,106,210,0.15) 0%, rgba(94,106,210,0.05) 100%)",
                      border: "1px solid rgba(94,106,210,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "14px",
                    }}
                  >
                    <Icon style={{ color: "#5e6ad2", width: "20px", height: "20px" }} />
                  </div>

                  <div
                    style={{
                      color: "#f7f8f8",
                      fontSize: "15px",
                      fontWeight: 500,
                      letterSpacing: "-0.3px",
                      marginBottom: "8px",
                    }}
                  >
                    {t(feat.title)}
                  </div>
                  <p style={{ color: "#8a8f98", fontSize: "14px", lineHeight: 1.5 }}>
                    {t(feat.body)}
                  </p>
                </div>
              </FadeInUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LandingFeatures;
